import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import * as SharedActions from './shared-filter.actions';
import {select, Store} from '@ngrx/store';
import {selectSelectedAdvancedFilterGroup} from './shared-filter.selectors';
import {
  IAdvancedFilterGroup,
} from '../../settings-new/models/general/advanced-filter-group.interface';
import {from, of} from 'rxjs';
import {
  CollectionStoresSettingsService,
} from '../../../shared/shared-services/firebase/collection-stores-settings.service';
import {path_stores_storeId_settings_advanced_filter} from '../../../shared/shared-services/database-paths';
import * as UserStoreSelectors from '../../user-access/user-stores-store/user-store.selectors';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import * as UserAccessSelectors from '../../user-access/user-store/user.selectors';
import {ErrorName} from '../../../shared/shared-components/components/global-error-display/models/global-error.enum';
import {selectAdvancedFilterGroups} from './shared-filter.selectors';
import {
  isGeneralAdvancedFilterGrid,
} from '../../settings-new/models/general/advanced-filter-grid.constants';


@Injectable()
export class SharedFilterEffects {

  // ====================================================================================================
  // Advanced Filter Group/s
  // ====================================================================================================

  // '[Shared][Advanced Filter Groups] Delete Advanced Filter Group Filter By Id',
  deleteAdvancedFilterGroupFilterById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SharedActions.deleteSelectedAdvancedFilterGroup,
      ),
      withLatestFrom(
        this.store.pipe(select(selectSelectedAdvancedFilterGroup)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, selectedAdvancedFilterGroup, store, user]) => {
        return from(this.collectionStoresSettingsService.deleteDocument(
          path_stores_storeId_settings_advanced_filter(
            store.storeId,
            `${selectedAdvancedFilterGroup.id}`)),
        ).pipe(
          map(() => {
            return SharedActions.deleteSelectedAdvancedFilterGroupSuccess({id: selectedAdvancedFilterGroup.id});
          }),
          catchError((error) => of(
            SharedActions.deleteSelectedAdvancedFilterGroupFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  // '[Shared][Advanced Filter Groups] Get Advanced Filter Group By Id',
  getAdvancedFilterGroupById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getAdvancedFilterGroupById),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        return from(this.collectionStoresSettingsService.getDocument<IAdvancedFilterGroup>(
          path_stores_storeId_settings_advanced_filter(action.store.storeId, action.id.toString())),
        ).pipe(
          map((advancedFilterGroup: IAdvancedFilterGroup) => {
            return SharedActions.getAdvancedFilterGroupByIdSuccess({advancedFilterGroup});
          }),
          catchError((error) => of(
            SharedActions.getAdvancedFilterGroupByIdFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  // '[Shared][Advanced Filter Groups] Get Advanced Filter Groups'
  getAdvancedFilterGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.getAdvancedFilterGroups),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        return from(this.collectionStoresSettingsService.getStoreSettingsAdvancedFilterGroups(store)).pipe(
          map((advancedFilterGroups: IAdvancedFilterGroup[]) => {
            return SharedActions.getAdvancedFilterGroupsSuccess({advancedFilterGroups});
          }),
          catchError((error) => of(
              SharedActions.getAdvancedFilterGroupsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store?.storeId ?? 'unknown'],
                  userId: user?.id ?? 'unknown',
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  // '[Shared][Advanced Filter Groups] Save Selected Advanced Filter Group'
  saveSelectedAdvancedFilterGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SharedActions.saveSelectedAdvancedFilterGroup,
      ),
      withLatestFrom(
        this.store.pipe(select(selectSelectedAdvancedFilterGroup)),
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, selectedAdvancedFilterGroup, store, user]) => {
        const selectedGroup = action.advancedFilterGroup ?? selectedAdvancedFilterGroup;
        if (!selectedGroup?.id) {
          return of(
            SharedActions.saveSelectedAdvancedFilterGroupFailure({
              error: {
                action: JSON.stringify(action),
                actionType: action.type,
                name: ErrorName.ActionFailedError,
                originalError: {message: 'Missing advanced filter id while saving filter group.'},
                message: 'Missing advanced filter id while saving filter group.',
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            }),
          );
        }
        const advancedFilterGroup: IAdvancedFilterGroup = {
          ...selectedGroup,
          saved: true,
          lastModified: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        };
        return from(this.collectionStoresSettingsService.setDocument<IAdvancedFilterGroup>(
          path_stores_storeId_settings_advanced_filter(store.storeId, advancedFilterGroup.id.toString()), advancedFilterGroup)).pipe(
          map(() => {
            return SharedActions.saveSelectedAdvancedFilterGroupSuccess({id: advancedFilterGroup.id});
          }),
          catchError((error) => of(
            SharedActions.saveSelectedAdvancedFilterGroupFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            })),
          ),
        );
      }),
    ),
  );

  // '[Shared][Advanced Filter Groups] Save Selected Advanced Filter Group Success',
  saveSelectedAdvancedFilterGroupSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.saveSelectedAdvancedFilterGroupSuccess),
      withLatestFrom(this.store.pipe(select(selectSelectedStore))),
      mergeMap(([{id}, store]) => {
        return of(SharedActions.getAdvancedFilterGroupById({id, store}));
      }),
    ),
  );

  // '[Shared][Advanced Filter Groups] Copy Advanced Filter Groups Between Stores'
  copyAdvancedFilterGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.copyAdvancedFilterGroups),
      withLatestFrom(
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, selectedStore, user]) =>
        from(this.collectionStoresSettingsService.copyStoreSettingsAdvancedFilterGroups(action.sourceStore, action.targetStore, action.filterIds)).pipe(
          mergeMap(({copiedCount, skippedCount, failures}) => {
            const followUps = [
              SharedActions.copyAdvancedFilterGroupsSuccess({
                targetStore: action.targetStore,
                copiedCount,
                skippedCount,
                failures,
              }),
            ];
            if (selectedStore?.storeId === action.targetStore.storeId && copiedCount > 0) {
              followUps.push(SharedActions.getAdvancedFilterGroups() as unknown as ReturnType<typeof SharedActions.copyAdvancedFilterGroupsSuccess>);
            }
            return followUps;
          }),
          catchError((error) => of(
            SharedActions.copyAdvancedFilterGroupsFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [action.sourceStore?.storeId ?? 'unknown', action.targetStore?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            }),
          )),
        ),
      ),
    ),
  );

  copyGeneralFiltersToStockTake$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedActions.copyGeneralFiltersToStockTake),
      withLatestFrom(
        this.store.pipe(select(selectAdvancedFilterGroups)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      exhaustMap(([action, allFilters, user]) => {
        const idSet = new Set<number>(action.filterIds);
        const sourceFilters = allFilters.filter((filter: IAdvancedFilterGroup): boolean =>
          idSet.has(filter.id) && filter.saved && isGeneralAdvancedFilterGrid(filter.grid),
        );
        return from(
          this.collectionStoresSettingsService.copyGeneralAdvancedFiltersToStockTake(action.store, sourceFilters),
        ).pipe(
          mergeMap(({copiedCount, skippedCount, failures}) => {
            const followUps = [
              SharedActions.copyGeneralFiltersToStockTakeSuccess({copiedCount, skippedCount, failures}),
            ];
            if (copiedCount > 0) {
              followUps.push(
                SharedActions.getAdvancedFilterGroups() as unknown as ReturnType<
                  typeof SharedActions.copyGeneralFiltersToStockTakeSuccess
                >,
              );
            }
            return followUps;
          }),
          catchError((error) => of(
            SharedActions.copyGeneralFiltersToStockTakeFailure({
              error: {
                ...error,
                action: JSON.stringify(action),
                actionType: action.type,
                originalError: error,
                storeIds: [action.store?.storeId ?? 'unknown'],
                userId: user?.id ?? 'unknown',
              },
            }),
          )),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private collectionStoresSettingsService: CollectionStoresSettingsService,
    private readonly store: Store,
  ) {
  }
}
